import React, { FC, useCallback, useRef } from 'react'
import {
  Banner,
  Button,
  ModalActions,
  ModalContent,
} from '@lightspeed/design-system-react'
import {
  PaymentMethodForm,
  PaymentMethodFormRef,
} from '@vend/business-components'
import { useT } from '@transifex/react'
import { trackUserEvent } from '@vend/utilities'
import { usePaymentFormSubmission } from '../../hooks/usePaymentFormSubmission'
import {
  PAYMENT_METHOD_ON_FILE_INFO_TERMS_TEXT,
  PAYMENT_METHOD_ON_FILE_ADDITIONAL_TERMS_TEXT,
} from '../../../../../constants/payment-method-on-file-terms'
import { WEB_REGISTER } from '../../../../web-register.constant'
import {
  PMOF_SAVE_CHECKBOX_ENABLED,
  PMOF_SAVE_CHECKBOX_DISABLED,
} from '../../helpers/lspayConstants'
import { useTheme } from '../../hooks/useTheme'
import { ExpandableTermsSection } from './ExpandableTermsSection'

type PaymentMethodFormOnChange = NonNullable<
  React.ComponentProps<typeof PaymentMethodForm>['onChange']
>
type PaymentFormChangeEvent = Parameters<PaymentMethodFormOnChange>[0]

type Props = {
  session: string
  customerId?: string
  saleId?: string
  paymentId?: string
  onVerifyingPaymentMethodStateChange: (isSubmitting: boolean) => void
  onClose: () => void
  onComplete: () => void
  onError: (message: string) => void
  isMotoWithSavePayment: boolean
}

export const LSPAYCnpPmofPaymentForm: FC<Props> = ({
  session,
  customerId,
  saleId,
  paymentId,
  onVerifyingPaymentMethodStateChange,
  onComplete,
  onClose,
  onError,
  isMotoWithSavePayment,
}) => {
  const paymentFormRef = useRef<PaymentMethodFormRef>(null)
  const t = useT()
  const theme = useTheme()

  const {
    isSubmitting,
    bannerErrorMessage,
    handleProcess,
  } = usePaymentFormSubmission({
    onVerifyingPaymentMethodStateChange,
    onComplete,
    onError,
  })

  const previousSaveCheckboxChecked = useRef<boolean | undefined>(undefined)

  const handlePaymentFormChange = useCallback(
    (event: PaymentFormChangeEvent) => {
      if (event.status !== 'Change') return
      if (
        event.saveCheckboxChecked === undefined ||
        event.saveCheckboxChecked === previousSaveCheckboxChecked.current
      ) {
        return
      }
      previousSaveCheckboxChecked.current = event.saveCheckboxChecked

      const metadata: Record<string, unknown> = {
        customerId,
        paymentMethodType: event.paymentMethodType,
      }
      if (saleId) {
        metadata.saleId = saleId
      }
      if (paymentId) {
        metadata.paymentId = paymentId
      }

      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        event.saveCheckboxChecked
          ? PMOF_SAVE_CHECKBOX_ENABLED
          : PMOF_SAVE_CHECKBOX_DISABLED,
        metadata
      )
    },
    [customerId, saleId, paymentId]
  )

  return (
    <>
      <ModalContent>
        {bannerErrorMessage && (
          <div className="vd-mt3 vd-mb3">
            <Banner variant="negative">{bannerErrorMessage}</Banner>
          </div>
        )}
        <PaymentMethodForm
          ref={paymentFormRef}
          session={session}
          theme={theme}
          onChange={handlePaymentFormChange}
        />
        {isMotoWithSavePayment && (
          <div className="vd-pb3" data-testid="payment-method-on-file-terms">
            <p className="vd-text--body vd-mb3">
              {t(PAYMENT_METHOD_ON_FILE_INFO_TERMS_TEXT, {
                _context:
                  'Terms of service information text for Payment Method on File feature',
              })}
            </p>
            <ExpandableTermsSection
              title="Additional terms & conditions"
              titleContext="Additional terms and conditions expandable section title"
              content={PAYMENT_METHOD_ON_FILE_ADDITIONAL_TERMS_TEXT}
              contentContext="Additional terms and conditions liability disclaimer text"
              testId="additional-terms"
            />
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <div className="vd-btn-group">
          <Button
            data-track="add-payment-method-cancel"
            variant="supplementary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('Cancel payment', {
              _context: 'Cancel payment button in manual card entry form',
            })}
          </Button>
          <Button
            data-track="add-payment-method-submit"
            variant="go"
            onClick={() => handleProcess(paymentFormRef)}
            loading={isSubmitting}
            disabled={!session || isSubmitting}
          >
            {t('Process payment', {
              _context: 'Process payment button in manual card entry form',
            })}
          </Button>
        </div>
      </ModalActions>
    </>
  )
}
